import { Controller } from "@hotwired/stimulus"

// Kontroler kalendarza zadań (Organize::Task). Uproszczona wersja calendar-dashboard:
// - brak modala "szybkiego tworzenia" - kliknięcie pustego dnia robi redirect na /organize/tasks/new
//   z prefillem deadline w query (?deadline=YYYY-MM-DD lub YYYY-MM-DDTHH:MM dla widoku tygodniowego).
// - kliknięcie zadania pokazuje popover z detalami (status, responsible, klient, recurring).
// - obsługuje też przycisk "więcej zadań" na dniu (popover z pełną listą) i widok mobilny.
export default class extends Controller {
  static targets = [
    "dayEventsPopover", "dayEventsPanel", "dayEventsTitle", "dayEventsList",
    "eventDetail", "eventDetailPanel", "eventDetailName", "eventDetailSid", "eventDetailColor",
    "eventDetailTime", "eventDetailStatusRow", "eventDetailStatus",
    "eventDetailResponsibleRow", "eventDetailResponsible",
    "eventDetailRecurrenceRow", "eventDetailRecurrence",
    "eventDetailClientRow", "eventDetailClient",
    "eventDetailProjectRow", "eventDetailProject",
    "eventDetailTeamRow", "eventDetailTeam",
    "eventDetailPriority",
    "eventDetailEstimatedRow", "eventDetailEstimated",
    "eventDetailTagsRow", "eventDetailTags",
    "eventDetailDesc", "eventDetailShowLink", "eventDetailEditLink",
  ]

  static values = {
    newTaskUrl: String,
    eventsData: Object,
    selectedDate: String,
  }

  // Kliknięcie pustego dnia/komórki/kolumny - redirect do /organize/tasks/new z prefillem terminu.
  // Dla widoku tygodniowego liczy też godzinę na podstawie Y kliknięcia w kolumnie.
  openNewTask(event) {
    // Nie reaguj jeśli kliknięto na child (np. button zadania) - tylko bezpośredni klik w tło dnia.
    if (event.target !== event.currentTarget) return

    const date = event.currentTarget.dataset.date
    if (!date) return

    let deadline = date
    const rect = event.currentTarget.getBoundingClientRect()
    // jeśli kolumna jest wysoka (>200px) - traktuj jako siatka godzinowa i policz godzinę
    if (rect.height > 200) {
      const clickY = event.clientY - rect.top
      const totalMinutes = Math.round((clickY / rect.height) * 24 * 60)
      const hour = Math.min(Math.max(Math.floor(totalMinutes / 60), 0), 23)
      const minutes = Math.round(totalMinutes / 30) * 30 % 60
      const pad = (n) => String(n).padStart(2, "0")
      deadline = `${date}T${pad(hour)}:${pad(minutes)}`
    }

    const url = new URL(this.newTaskUrlValue, window.location.origin)
    url.searchParams.set("deadline", deadline)
    window.location.href = url.toString()
  }

  // Mobile: zaznacz dzień (analogicznie do calendar-dashboard).
  selectDay(event) {
    const button = event.currentTarget
    this.element.querySelectorAll("button[data-is-selected]").forEach(btn => {
      btn.removeAttribute("data-is-selected")
    })
    button.setAttribute("data-is-selected", "")
    this.selectedDateValue = button.dataset.date
  }

  // Popover z pełną listą zadań danego dnia (po kliknięciu "+N więcej").
  showDayEvents(event) {
    event.stopPropagation()
    const date = event.currentTarget.dataset.date
    const dayData = this.eventsDataValue[date] || {}
    const events = dayData.events || []

    this.dayEventsTitleTarget.textContent = dayData.title || date

    const items = events.map(ev => {
      const evData = JSON.stringify(ev)
      const priorityHtml = this._priorityIconHtml(ev.priority)
      const opacityClass = ev.read_only ? "opacity-70" : ""
      return `<li>
        <button type="button" data-event='${this._escapeAttr(evData)}' data-action="click->task-calendar#showEventDetail" class="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50 w-full text-left ${opacityClass}">
          <span class="size-2 shrink-0 rounded-full" style="background-color: ${this._escapeHtml(ev.color)};"></span>
          <span class="text-xs text-gray-500">${this._escapeHtml(ev.time)}</span>
          ${priorityHtml ? `<span class="shrink-0">${priorityHtml}</span>` : ""}
          <span class="flex-auto truncate text-sm font-medium text-gray-900 group-hover:text-blue-600">${this._escapeHtml(ev.name)}</span>
        </button>
      </li>`
    }).join("")
    this.dayEventsListTarget.innerHTML = items

    const btn = event.currentTarget
    const rect = btn.getBoundingClientRect()
    const panel = this.dayEventsPanelTarget
    this.dayEventsPopoverTarget.classList.remove("hidden")

    const panelWidth = 384
    let left = rect.left + rect.width / 2 - panelWidth / 2
    let top = rect.bottom + 4
    if (left < 8) left = 8
    if (left + panelWidth > window.innerWidth - 8) left = window.innerWidth - panelWidth - 8
    if (top + panel.offsetHeight > window.innerHeight - 8) top = rect.top - panel.offsetHeight - 4
    panel.style.left = `${left}px`
    panel.style.top = `${top}px`
  }

  closeDayEvents() {
    this.dayEventsPopoverTarget.classList.add("hidden")
  }

  // Popover z detalami pojedynczego zadania.
  showEventDetail(event) {
    event.stopPropagation()
    if (!this.hasEventDetailTarget) return

    const data = JSON.parse(event.currentTarget.dataset.event)

    this.eventDetailNameTarget.textContent = data.name
    this.eventDetailColorTarget.style.backgroundColor = data.color || "#3b82f6"
    this.eventDetailTimeTarget.textContent = data.time

    if (data.sid && this.hasEventDetailSidTarget) {
      this.eventDetailSidTarget.textContent = data.sid
      this.eventDetailSidTarget.classList.remove("hidden")
    } else if (this.hasEventDetailSidTarget) {
      this.eventDetailSidTarget.classList.add("hidden")
    }

    // show_url może być nullem dla wirtualnych recurringów - wtedy ukrywamy link
    if (data.show_url) {
      this.eventDetailShowLinkTarget.href = data.show_url
      this.eventDetailShowLinkTarget.classList.remove("hidden")
    } else {
      this.eventDetailShowLinkTarget.classList.add("hidden")
    }
    if (data.edit_url) {
      this.eventDetailEditLinkTarget.href = data.edit_url
      this.eventDetailEditLinkTarget.classList.remove("hidden")
    } else {
      this.eventDetailEditLinkTarget.classList.add("hidden")
    }

    this._toggleRow("Status", data.status_name)
    this._toggleRow("Responsible", data.responsible_name)
    this._toggleRow("Recurrence", data.recurrence)
    this._togglePriority(data.priority)
    this._toggleRow("Project", data.project_name)
    this._toggleRow("Team", data.team_name)
    this._toggleRow("Estimated", data.estimated_hours ? `${data.estimated_hours} h` : null)
    this._toggleTags(data.tag_names)

    if (data.client_name && this.hasEventDetailClientRowTarget) {
      this.eventDetailClientTarget.textContent = data.client_name
      if (data.client_url) this.eventDetailClientTarget.href = data.client_url
      this.eventDetailClientRowTarget.classList.remove("hidden")
    } else if (this.hasEventDetailClientRowTarget) {
      this.eventDetailClientRowTarget.classList.add("hidden")
    }

    if (data.description) {
      this.eventDetailDescTarget.textContent = data.description
      this.eventDetailDescTarget.classList.remove("hidden")
    } else {
      this.eventDetailDescTarget.classList.add("hidden")
    }

    this.eventDetailTarget.classList.remove("hidden")

    const rect = event.currentTarget.getBoundingClientRect()
    const panel = this.eventDetailPanelTarget
    const panelWidth = 288
    let left = rect.right + 8
    let top = rect.top

    if (left + panelWidth > window.innerWidth - 8) left = rect.left - panelWidth - 8
    if (left < 8) left = 8

    panel.style.left = `${left}px`
    panel.style.top = `${top}px`

    requestAnimationFrame(() => {
      if (top + panel.offsetHeight > window.innerHeight - 8) {
        top = window.innerHeight - panel.offsetHeight - 8
      }
      if (top < 8) top = 8
      panel.style.top = `${top}px`
    })
  }

  closeEventDetail() {
    if (this.hasEventDetailTarget) {
      this.eventDetailTarget.classList.add("hidden")
    }
  }

  // Pokazuje ikonę priorytetu obok tytułu (null = default normalny, ukryj).
  _togglePriority(value) {
    if (!this.hasEventDetailPriorityTarget) return
    const variants = this.eventDetailPriorityTarget.querySelectorAll("[data-priority-value]")
    variants.forEach(el => el.classList.add("hidden"))
    if (value === null || value === undefined) {
      this.eventDetailPriorityTarget.classList.add("hidden")
      return
    }
    const match = this.eventDetailPriorityTarget.querySelector(`[data-priority-value="${value}"]`)
    if (!match) {
      this.eventDetailPriorityTarget.classList.add("hidden")
      return
    }
    match.classList.remove("hidden")
    this.eventDetailPriorityTarget.classList.remove("hidden")
  }

  // Zwraca HTML ikony priorytetu (klonowany ze schowanego template'u w eventDetailPriorityTarget)
  // do osadzenia w innych miejscach (np. dayEvents popover).
  _priorityIconHtml(value) {
    if (value === null || value === undefined) return ""
    if (!this.hasEventDetailPriorityTarget) return ""
    const match = this.eventDetailPriorityTarget.querySelector(`[data-priority-value="${value}"]`)
    if (!match) return ""
    // innerHTML to <div class="relative h-X w-X"><svg .../></div> - zwracamy bez klasy "hidden"
    return match.innerHTML
  }

  // Renderuje tagi jako kolorowe pigułki (lub ukrywa wiersz, gdy brak).
  _toggleTags(tagNames) {
    if (!this.hasEventDetailTagsRowTarget || !this.hasEventDetailTagsTarget) return
    if (!tagNames || tagNames.length === 0) {
      this.eventDetailTagsRowTarget.classList.add("hidden")
      return
    }
    this.eventDetailTagsTarget.innerHTML = tagNames.map(name =>
      `<span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 mr-1">${this._escapeHtml(name)}</span>`,
    ).join("")
    this.eventDetailTagsRowTarget.classList.remove("hidden")
  }

  // Pokazuje/ukrywa wiersz popovera w zależności od dostępności wartości.
  _toggleRow(suffix, value) {
    const rowKey = `eventDetail${suffix}Row`
    const valKey = `eventDetail${suffix}`
    const hasRow = this[`has${rowKey.charAt(0).toUpperCase() + rowKey.slice(1)}Target`]
    const hasVal = this[`has${valKey.charAt(0).toUpperCase() + valKey.slice(1)}Target`]
    if (!hasRow || !hasVal) return
    const rowEl = this[`${rowKey}Target`]
    const valEl = this[`${valKey}Target`]
    if (value) {
      valEl.textContent = value
      rowEl.classList.remove("hidden")
    } else {
      rowEl.classList.add("hidden")
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  _escapeHtml(str) {
    if (str == null) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  _escapeAttr(str) {
    if (str == null) return ""
    return str.replace(/&/g, "&amp;").replace(/'/g, "&#39;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }
}
